import { createSelector, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { breakSeasonTeamAbrvID } from '@shared/helpers';
import { AccumulatedTeamCategoriesByGameStats, SeasonID, SeasonTeamAbrvID } from '@shared/types';
import { Cls, Emo } from 'app/constants';
import { AppThunk, RootState } from 'types';

export interface TeamStatsState {
  weeklyStatsBySeason: Record<SeasonID, AccumulatedTeamCategoriesByGameStats>;
  cumulativeStatsBySeason: Record<SeasonID, AccumulatedTeamCategoriesByGameStats>;
  ranksBySeason: Record<SeasonID, AccumulatedTeamCategoriesByGameStats>;
  loadedSeasons: SeasonID[];
  loadTeamStatsWithOverride: boolean;
  seasonIDToLoad?: SeasonID;
}

const initialState: TeamStatsState = {
  weeklyStatsBySeason: {},
  cumulativeStatsBySeason: {},
  ranksBySeason: {},
  loadedSeasons: [],
  loadTeamStatsWithOverride: false,
  seasonIDToLoad: undefined,
};

/**
 * Thunk to load team stats for a specific season.
 *
 * @param {SeasonID} seasonID - The season to load stats for
 * @param {boolean} [withOverride=false] - Whether to force reload even if already loaded
 * @return {AppThunk} Redux thunk action
 */
export const loadTeamStatsBySeasonID =
  (seasonID: SeasonID, withOverride: boolean = false): AppThunk =>
  (dispatch, getState) => {
    if (getState().teamStats.loadedSeasons.includes(seasonID) && !withOverride) {
      console.log(`${Emo.info} Team Stats for %c ${seasonID} %c already loaded.`, Cls.Val, '');
      return;
    }
    dispatch(setSeasonIDToLoad({ seasonID, withOverride }));
  };

export const teamStatsSlice = createSlice({
  name: 'teamStats',
  initialState,
  reducers: {
    setTeamStatsSnapshotForSeason: (
      state,
      action: PayloadAction<{
        seasonID: SeasonID;
        weekly: AccumulatedTeamCategoriesByGameStats;
        cumulative: AccumulatedTeamCategoriesByGameStats;
        ranks: AccumulatedTeamCategoriesByGameStats;
      }>
    ) => {
      const { seasonID, weekly, cumulative, ranks } = action.payload;
      state.weeklyStatsBySeason[seasonID] = weekly;
      state.cumulativeStatsBySeason[seasonID] = cumulative;
      state.ranksBySeason[seasonID] = ranks;
      if (!state.loadedSeasons.includes(seasonID)) state.loadedSeasons.push(seasonID);
    },
    setTeamWeeklyStatsForSeason: (
      state,
      action: PayloadAction<{ seasonID: SeasonID; stats: AccumulatedTeamCategoriesByGameStats }>
    ) => {
      const { seasonID, stats } = action.payload;
      state.weeklyStatsBySeason[seasonID] = stats;
      if (!state.loadedSeasons.includes(seasonID)) {
        state.loadedSeasons.push(seasonID);
      }
    },
    setTeamCumulativeStatsForSeason: (
      state,
      action: PayloadAction<{ seasonID: SeasonID; stats: AccumulatedTeamCategoriesByGameStats }>
    ) => {
      const { seasonID, stats } = action.payload;
      state.cumulativeStatsBySeason[seasonID] = stats;
    },
    setTeamRanksForSeason: (
      state,
      action: PayloadAction<{ seasonID: SeasonID; stats: AccumulatedTeamCategoriesByGameStats }>
    ) => {
      const { seasonID, stats } = action.payload;
      state.ranksBySeason[seasonID] = stats;
      // Clear loading flags when all three are loaded
      if (
        state.weeklyStatsBySeason[seasonID] &&
        state.cumulativeStatsBySeason[seasonID] &&
        state.ranksBySeason[seasonID]
      ) {
        state.loadTeamStatsWithOverride = false;
        if (state.seasonIDToLoad === seasonID) {
          state.seasonIDToLoad = undefined;
        }
      }
    },
    setSeasonIDToLoad: (state, action: PayloadAction<{ seasonID: SeasonID | undefined; withOverride: boolean }>) => {
      state.seasonIDToLoad = action.payload.seasonID;
      state.loadTeamStatsWithOverride = action.payload.withOverride;
    },
    setLoadTeamStatsWithOverride: (state, action: PayloadAction<boolean>) => {
      state.loadTeamStatsWithOverride = action.payload;
    },
    resetTeamStats: () => initialState,
  },
});

export const {
  setTeamStatsSnapshotForSeason,
  setTeamWeeklyStatsForSeason,
  setTeamCumulativeStatsForSeason,
  setTeamRanksForSeason,
  setSeasonIDToLoad,
  setLoadTeamStatsWithOverride,
  resetTeamStats,
} = teamStatsSlice.actions;

// Base selectors
const selectTeamStatsState = (state: RootState) => state.teamStats;

/**
 * Select all weekly stats for a specific season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @return {AccumulatedTeamCategoriesByGameStats | undefined} Weekly stats by team
 */
export const selectTeamWeeklyStatsBySeason = createSelector(
  [selectTeamStatsState, (_state: RootState, seasonID: SeasonID) => seasonID],
  (teamStats, seasonID) => teamStats.weeklyStatsBySeason[seasonID]
);

/**
 * Select all cumulative stats for a specific season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @return {AccumulatedTeamCategoriesByGameStats | undefined} Cumulative stats by team
 */
export const selectTeamCumulativeStatsBySeason = createSelector(
  [selectTeamStatsState, (_state: RootState, seasonID: SeasonID) => seasonID],
  (teamStats, seasonID) => teamStats.cumulativeStatsBySeason[seasonID]
);

/**
 * Select all ranks for a specific season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @return {AccumulatedTeamCategoriesByGameStats | undefined} Ranks by team
 */
export const selectTeamRanksBySeason = createSelector(
  [selectTeamStatsState, (_state: RootState, seasonID: SeasonID) => seasonID],
  (teamStats, seasonID) => teamStats.ranksBySeason[seasonID]
);

/**
 * Select weekly stats for a specific team in a season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID - Team identifier with season prefix
 * @return {AccumulatedTeamStats | undefined} Team's weekly stats
 */
export const selectTeamWeeklyStats = createSelector(
  [
    (state: RootState, seasonID: SeasonID) => selectTeamWeeklyStatsBySeason(state, seasonID),
    (_state: RootState, _seasonID: SeasonID, seasonTeamAbrvID: SeasonTeamAbrvID) => seasonTeamAbrvID,
  ],
  (weeklyStats, seasonTeamAbrvID) => weeklyStats?.[seasonTeamAbrvID]
);

/**
 * Select cumulative stats for a specific team in a season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID - Team identifier with season prefix
 * @return {AccumulatedTeamStats | undefined} Team's cumulative stats
 */
export const selectTeamCumulativeStats = createSelector(
  [
    (state: RootState, seasonID: SeasonID) => selectTeamCumulativeStatsBySeason(state, seasonID),
    (_state: RootState, _seasonID: SeasonID, seasonTeamAbrvID: SeasonTeamAbrvID) => seasonTeamAbrvID,
  ],
  (cumulativeStats, seasonTeamAbrvID) => cumulativeStats?.[seasonTeamAbrvID]
);

/**
 * Select ranks for a specific team in a season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID - Team identifier with season prefix
 * @return {AccumulatedTeamStats | undefined} Team's ranks
 */
export const selectTeamRanks = createSelector(
  [
    (state: RootState, seasonID: SeasonID) => selectTeamRanksBySeason(state, seasonID),
    (_state: RootState, _seasonID: SeasonID, seasonTeamAbrvID: SeasonTeamAbrvID) => seasonTeamAbrvID,
  ],
  (ranks, seasonTeamAbrvID) => ranks?.[seasonTeamAbrvID]
);

/**
 * Select ranks for a specific team in a season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonTeamAbrvID} seasonTeamAbrvID - Team identifier with season prefix
 * @return {AccumulatedTeamStats | undefined} Team's ranks
 */
export const selectTeamRanksBySeasonTeamAbrvID = createSelector(
  [
    (state: RootState, seasonTeamAbrvID: SeasonTeamAbrvID) =>
      selectTeamRanksBySeason(state, breakSeasonTeamAbrvID(seasonTeamAbrvID).seasonID),
    (_state: RootState, seasonTeamAbrvID: SeasonTeamAbrvID) => seasonTeamAbrvID,
  ],
  (ranks, seasonTeamAbrvID) => ranks?.[seasonTeamAbrvID]
);

/**
 * Select list of season IDs that have team stats loaded.
 *
 * @param {RootState} state - Redux root state
 * @return {SeasonID[]} Array of loaded season IDs
 */
export const selectTeamStatsLoadedSeasonIDs = createSelector(
  [selectTeamStatsState],
  (teamStats) => teamStats.loadedSeasons
);

/**
 * Check if team stats are loaded for a specific season.
 *
 * @param {RootState} state - Redux root state
 * @param {SeasonID} seasonID - Season identifier
 * @return {boolean} True if season stats are loaded
 */
export const selectIsTeamStatsLoadedForSeason = createSelector(
  [selectTeamStatsLoadedSeasonIDs, (_state: RootState, seasonID: SeasonID) => seasonID],
  (loadedSeasons, seasonID) => loadedSeasons.includes(seasonID)
);

/**
 * Select the override flag for forcing team stats reload.
 *
 * @param {RootState} state - Redux root state
 * @return {boolean} Override flag state
 */
export const selectLoadTeamStatsWithOverride = createSelector(
  [selectTeamStatsState],
  (teamStats) => teamStats.loadTeamStatsWithOverride
);

/**
 * Select the season ID currently queued for loading.
 *
 * @param {RootState} state - Redux root state
 * @return {SeasonID | undefined} Season ID to load, if any
 */
export const selectSeasonIDToLoad = createSelector([selectTeamStatsState], (teamStats) => teamStats.seasonIDToLoad);

export default teamStatsSlice.reducer;
